import Debug from 'debug'
import debounce from 'lodash/debounce'
import Cookies from 'universal-cookie'
import z from 'zod'
import type { UseQueryOptions } from '@tanstack/react-query'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query'

import { queryClient } from '@jotta/query'
import { AppError } from '@jotta/types/AppError'
import { asTrilean } from '@jotta/utils/trilean'

const debug = Debug('jotta:auth')
const cookies = new Cookies()
const AuthStatusKey = ['authstatus']

const AuthStatusSchema = z.object({
  authenticated: z.boolean(),
  refreshable: z.boolean(),
  username: z.string().nullable(),
  expiresAt: z.number().nullable(),
  expiresInMillis: z.number().nullable(),
  refreshableInMillis: z.number().nullable(),
  version: z.string(),
})

async function parseResponse(res: Response) {
  const status = res.status
  const dateHeader = res.headers.get('date')
  if (!(status < 300 || status === 401 || status === 403)) {
    throw new AppError({
      message: res.statusText,
      HTTPStatus: status,
    })
  }
  if (!dateHeader) {
    throw new AppError({
      message: 'missing date header',
    })
  }

  const data = await res.json()
  const responseData = AuthStatusSchema.parse(data)
  return {
    serverTimeMillis: Date.parse(dateHeader),
    ...responseData,
  }
}

async function refetchAuthStatus() {
  await queryClient.invalidateQueries({ queryKey: AuthStatusKey })
  return queryClient.fetchQuery<ApiStatusType>({
    queryKey: AuthStatusKey,
    queryFn: getOrRefreshApiStatus,
  })
}

async function apiRefresh() {
  const log = debug.extend('/api/refresh')
  log('request')
  const res = await fetch('/api/refresh')
  log('response', res)

  return await parseResponse(res)
}

async function apiStatus() {
  const log = debug.extend('/api/status')
  try {
    log('request')
    const res = await fetch('/api/status')
    log('response', res)

    return await parseResponse(res)
  } catch (err) {
    // Something went wrong, try refresh
    log('response', err)
    return apiRefresh()
  }
}

let refreshTimeoutId = 0

async function getOrRefreshApiStatus(): Promise<{
  authenticated: boolean
  accessToken: string
  username: string | null
  serverTimeMillis: number
  version: string
}> {
  const log = debug.extend('getOrRefreshApiStatus')
  window.clearTimeout(refreshTimeoutId)
  refreshTimeoutId = 0
  let status = await apiStatus()

  if (status.refreshable) {
    status = await apiRefresh()
  }

  const {
    authenticated,
    username,
    refreshableInMillis: refreshInMillis,
    serverTimeMillis,
    version,
  } = status
  const accessToken = String(cookies.get('access_token'))

  if (typeof refreshInMillis === 'number' && refreshInMillis > 0) {
    const timeout = refreshInMillis + Math.round(Math.random() * 10_000)
    log(`Setting timeout for token refresh in ${timeout}ms`)
    refreshTimeoutId = window.setTimeout(() => refetchAuthStatus(), timeout)
  }

  return { authenticated, accessToken, username, serverTimeMillis, version }
}

type ApiStatusType = Awaited<ReturnType<typeof getOrRefreshApiStatus>>

const configQueryOptions = {
  retry: true, // don't fail and display error page by never giving up resolving the auth status
  queryKey: AuthStatusKey,
  queryFn: getOrRefreshApiStatus,
  placeholderData: keepPreviousData,
  refetchOnMount: false,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
} satisfies UseQueryOptions<ApiStatusType>

export function useAuthStatus() {
  return useQuery(configQueryOptions)
}

export function useAuthStatusSuspense() {
  return useSuspenseQuery(configQueryOptions)
}

export function useAuthenticated() {
  return asTrilean(useAuthStatus().data?.authenticated)
}

export function ensureAuthStatus() {
  return queryClient.ensureQueryData(configQueryOptions)
}

export function getAuthStatus() {
  return queryClient.getQueryData<ApiStatusType>(AuthStatusKey)
}

export function getAccessToken() {
  return getAuthStatus()?.accessToken
}

export async function isAuthenticated() {
  return (await ensureAuthStatus()).authenticated
}

const refreshAccessToken = debounce(
  async () => {
    const res = await refetchAuthStatus()
    return res.authenticated ? res.accessToken : ''
  },
  4_000,
  { leading: true, trailing: false, maxWait: 4_000 },
)

export function useAccessToken() {
  return useAuthStatus().data?.accessToken
}

export function useRefreshAccessToken() {
  return useMutation({ mutationFn: refreshAccessToken })
}

export async function getOrRefreshAccessToken() {
  const status = await ensureAuthStatus()

  if (status.authenticated) {
    return status.accessToken
  }

  return refreshAccessToken()
}
